import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store/rootReducer';
import {
    Button,
    Badge,
    Card,
    Typography,
    Row,
    Col,
    Modal,
    Switch,
    message,
    Tooltip,
    Collapse,
    Image as AntImage
} from 'antd';
import {CopyOutlined} from '@ant-design/icons';
import {getCookie} from 'typescript-cookie';
import campaignLinkService from '../../../services/campaignLinkService';
import {downloadFile, formatValue, roundedRect, useIsMobile} from '../../../utils/helpers';
import {FiDownload} from "react-icons/fi";
import QRCode from "qrcode";
import RichTextViewer from "../../../components/molecules/Forms/RichText/viewer";
import {trackEvent} from "../../../analytics/posthog";

const {Text, Link} = Typography;

const CampaignLinkDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [campaignLinkData, setCampaignLinkData] = useState<any | null>(null);
    const [assets, setAssets] = useState<any | null>(null);
    const [earnings, setEarnings] = useState<any | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [toggleActive, setToggleActive] = useState(false);
    const currentUser: any = useSelector((state: RootState) => state.auth.user);
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType;
    const isMobile = useIsMobile();

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [isBlocked, setIsBlocked] = useState(false);
    
    const fetchCampaignLinkDetails = async () => {
        try {
            const data = await campaignLinkService.getCampaignLinkById(id as string);
            setCampaignLinkData(data.campaignLink);
            setAssets(data.assets);
            setEarnings(data.earnings);
            setIsBlocked(data.isBlocked || false);
        } catch (error) {
            console.error('Error fetching campaign details:', error);
        }
    };

    const handleUpdateLinkStatus = async () => {
        try {
            const result = await campaignLinkService.updateCampaignLink(id as string, {isActive: toggleActive});
            if (result._id) {
                message.success('Campaign link updated successfully.');
                await fetchCampaignLinkDetails();
            }
        } catch (error: any) {
            message.error(error.message || 'Update failed. Please try again.');
        } finally {
            setIsModalVisible(false);
        }
    };

    const handleCopy = () => {
        if (!campaignLinkData.customUrl) return;
        navigator.clipboard
            .writeText(campaignLinkData.customUrl)
            .then(() => {
                trackEvent('affiliate_link_copied_to_clipboard', {
                    role,
                    campaign_link_id: id,
                    campaign_id: campaignLinkData.campaignId?._id
                });
                message.success('Link copied to clipboard!');
            })
            .catch(() => message.error('Failed to copy link.'));
    };

    useEffect(() => {
        fetchCampaignLinkDetails();
    }, []);

    useEffect(() => {
        if (campaignLinkData?.isActive !== undefined) setToggleActive(campaignLinkData.isActive);
    }, [campaignLinkData]);

    if (!campaignLinkData) return <p>Loading campaign link details...</p>;

    const {campaignId, ownerId, customUrl, clickCounter, isActive} = campaignLinkData;

    const locationParts = [
        campaignLinkData.geographicLocation?.city,
        campaignLinkData.geographicLocation?.state,
        campaignLinkData.geographicLocation?.country
    ].filter(Boolean);
    const location = locationParts.length ? locationParts.join(', ') : 'Global';

    const isOwner = currentUser?.userId === ownerId;

    const logoUrl = assets.logo?.signedUrl || null;
    const thumbnailUrl = assets.thumbnail?.signedUrl || null;
    const mediaUrl = assets.media?.signedUrl || null;
    const videoPreviewUrl = assets.video?.compressed?.signedUrl || null;
    const videoDownloadUrl = assets.video?.original?.signedUrl || null;

    const handleAssetDownload = (url: string | null, assetType: string) => {
        if (!url) return;

        downloadFile(url);

        if (['promoter', 'partner'].includes(role)) {
            trackEvent('campaign_assets_downloaded', {
                role,
                campaign_link_id: id,
                campaign_id: campaignId?._id,
                campaign_status: campaignId?.status,
                campaign_asset_type: assetType
            });
        }
    };

    const downloadQRWithLogo = async (url: string, logoPath: string) => {
        if (!url) return;

        try {
            const canvas = document.createElement("canvas");
            await QRCode.toCanvas(canvas, url, {
                width: 500,
                margin: 2,
                color: {dark: "#000000", light: "#ffffff"}
            });

            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = logoPath;

            img.onload = () => {
                const centerX = canvas.width / 2;
                const centerY = canvas.height / 2;

                const size = canvas.width;
                const logoSize = Math.round(size * 0.16);

                const padding = Math.round(size * 0.035);
                const badgeSize = logoSize + padding * 2;
                const radius = Math.round(badgeSize * 0.22);

                const x = Math.round((size - badgeSize) / 2);
                const y = Math.round((size - badgeSize) / 2);

                ctx.save();
                ctx.fillStyle = "#ffffff";
                roundedRect(ctx, x, y, badgeSize, badgeSize, radius);
                ctx.fill();
                ctx.restore();

                ctx.drawImage(img, centerX - logoSize / 2, centerY - logoSize / 2, logoSize, logoSize);

                const dataUrl = canvas.toDataURL("image/png");
                const link = document.createElement("a");
                link.href = dataUrl;
                link.download = "qr-with-logo.png";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                trackEvent('affliate_link_qr_code_dowloaded', {
                    role,
                    campaign_link_id: id,
                    campaign_id: campaignId?._id
                });
            };
        } catch (err) {
            console.error("Error generating QR with logo", err);
        }
    };

    return (
        <div style={{width: '100%', maxWidth: 1100, margin: '0 auto', padding: isMobile ? 12 : 20}}>
            {/* Title Section */}
            <label className={"title"}>Campaign link details</label>

            {/* Info Section */}
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card>
                        <div style={{marginBottom: 12}}><Text strong>Title:</Text> {campaignId.title}</div>

                        <div style={{marginBottom: 12}}>
                            <Text strong>Description:</Text>
                            <div className="rtx-viewer" style={{marginTop: 6}}>
                                <RichTextViewer html={campaignId.description}/>
                            </div>
                        </div>

                        <div style={{marginBottom: 12}}>
                            <Text strong>Target URL: </Text>
                            <Link href={campaignId.targetURL} target="_blank" rel="noopener noreferrer"
                                  style={{wordBreak: 'break-all'}}>
                                {campaignId.targetURL}
                            </Link>
                        </div>

                        <div>
                            <Text strong>Geographic location:</Text> {location}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Metrics Section */}
            <Row gutter={[16, 16]} style={{marginTop: 20}}>
                <Col xs={24} sm={12} md={8}><Card title="All clicks">{campaignId.clickCounter}</Card></Col>
                <Col xs={24} sm={12} md={8}><Card title="Click rate">${formatValue(campaignId.clickRate)}</Card></Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Status">
                        <Badge
                            status={campaignId.status === "cancelled" ? "error" : "success"}
                            text={
                                <Text style={{color: campaignId.status === "cancelled" ? "red" : "green"}}>
                                    {campaignId.status.charAt(0).toUpperCase() + campaignId.status.slice(1)}
                                </Text>
                            }
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{marginTop: 10}}>
                <Col xs={24} sm={12} md={8}><Card title="Initial budget">${formatValue(campaignId.initialBudget)}</Card></Col>
                <Col xs={24} sm={12} md={8}><Card title="Balance">${formatValue(campaignId.balance)}</Card></Col>
                <Col xs={24} sm={12} md={8}><Card title="Expended funds">${formatValue(campaignId.expendedFunds)}</Card></Col>
            </Row>

            <Row gutter={[16, 16]} style={{marginTop: 10}}>
                <Col span={24}>
                    <Collapse
                        defaultActiveKey={[]}
                        items={[
                            {
                                key: 'assets',
                                label: 'Campaign Assets',
                                children: (
                                    <Row gutter={[16, 16]}>
                                        {logoUrl && (
                                            <Col xs={24} sm={12} md={8}>
                                                <Card
                                                    size="small"
                                                    title="Logo"
                                                    actions={[
                                                        <Button key="download-logo" type="link"
                                                                onClick={() => handleAssetDownload(logoUrl, 'logo')}>
                                                            Download
                                                        </Button>,
                                                    ]}
                                                >
                                                    <AntImage
                                                        src={logoUrl}
                                                        preview={false}
                                                        style={{
                                                            width: '100%',
                                                            aspectRatio: '4 / 3',
                                                            objectFit: 'cover',
                                                            borderRadius: 6,
                                                        }}
                                                        onClick={() => {
                                                            setPreviewImage(logoUrl);
                                                            setPreviewVisible(true);
                                                        }}
                                                    />
                                                </Card>
                                            </Col>
                                        )}

                                        {thumbnailUrl && (
                                            <Col xs={24} sm={12} md={8}>
                                                <Card
                                                    size="small"
                                                    title="Thumbnail"
                                                    actions={[
                                                        <Button
                                                            key="download-thumbnail"
                                                            type="link"
                                                            onClick={() => handleAssetDownload(thumbnailUrl, 'thumbnail')}
                                                        >
                                                            Download
                                                        </Button>,
                                                    ]}
                                                >
                                                    <AntImage
                                                        src={thumbnailUrl}
                                                        preview={false}
                                                        style={{
                                                            width: '100%',
                                                            aspectRatio: '4 / 3',
                                                            objectFit: 'cover',
                                                            borderRadius: 6,
                                                        }}
                                                        onClick={() => {
                                                            setPreviewImage(thumbnailUrl);
                                                            setPreviewVisible(true);
                                                        }}
                                                    />
                                                </Card>
                                            </Col>
                                        )}

                                        {mediaUrl && (
                                            <Col xs={24} sm={12} md={8}>
                                                <Card
                                                    size="small"
                                                    title="Media"
                                                    actions={[
                                                        <Button key="download-media" type="link"
                                                                onClick={() => handleAssetDownload(mediaUrl, 'media')}>
                                                            Download
                                                        </Button>,
                                                    ]}
                                                >
                                                    <AntImage
                                                        src={mediaUrl}
                                                        preview={false}
                                                        style={{
                                                            width: '100%',
                                                            aspectRatio: '4 / 3',
                                                            objectFit: 'cover',
                                                            borderRadius: 6,
                                                        }}
                                                        onClick={() => {
                                                            setPreviewImage(mediaUrl);
                                                            setPreviewVisible(true);
                                                        }}
                                                    />
                                                </Card>
                                            </Col>
                                        )}

                                        {videoPreviewUrl && (
                                            <Col xs={24} sm={12} md={8}>
                                                <Card
                                                    size="small"
                                                    title="Video"
                                                    actions={[
                                                        <Button
                                                            key="download-video"
                                                            type="link"
                                                            onClick={() => handleAssetDownload(videoDownloadUrl, 'video')}
                                                        >
                                                            Download
                                                        </Button>,
                                                    ]}
                                                >
                                                    <video
                                                        src={videoPreviewUrl}
                                                        controls
                                                        preload="metadata"
                                                        style={{
                                                            width: '100%',
                                                            borderRadius: 6,
                                                            maxHeight: 300,
                                                            objectFit: 'cover'
                                                        }}
                                                    >
                                                        Your browser does not support video.
                                                    </video>
                                                </Card>
                                            </Col>
                                        )}

                                        {!logoUrl && !thumbnailUrl && !mediaUrl && !videoPreviewUrl && (
                                            <Col span={24}>
                                                <Text type="secondary">No assets uploaded for this campaign.</Text>
                                            </Col>
                                        )}
                                    </Row>
                                ),
                            },
                        ]}
                    />
                </Col>
            </Row>

            <Modal
                open={previewVisible}
                footer={null}
                onCancel={() => {
                    setPreviewVisible(false);
                    setPreviewImage(null);
                }}
                width={400}
                centered
            >
                {previewImage && <AntImage src={previewImage} alt="Preview" style={{width: '100%'}}/>}
            </Modal>

            <Card title="Campaign Link" style={{marginTop: 30}}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Card type="inner" title="Affiliate link">
                            <Link href={customUrl} target="_blank" rel="noopener noreferrer">
                                Link here
                            </Link>

                            <Tooltip title="Click to copy">
                                <Button
                                    type="text"
                                    icon={<CopyOutlined style={{fontSize: 15}}/>}
                                    onClick={handleCopy}
                                />
                            </Tooltip>

                            <Tooltip title="Download QR">
                                <Button
                                    type="text"
                                    icon={<FiDownload style={{fontSize: 15}}/>}
                                    onClick={() =>
                                        downloadQRWithLogo(customUrl, "/prmtf-icon-blue.png")
                                    }
                                />
                            </Tooltip>
                        </Card>
                    </Col>
                </Row>
                <Row gutter={[16, 16]} style={{marginTop: 10}}>
                    <Col xs={24} sm={12} md={8}>
                        <Card type="inner" title="Clicks">
                            <Text>{clickCounter}</Text>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Card type="inner" title="Earnings">
                            <Text>
                                ${formatValue(earnings)}
                            </Text>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Card type="inner" title="Link status">
                            <Badge
                                status={isActive ? "success" : "error"}
                                text={
                                    <Text style={{color: isActive ? "green" : "red"}}>
                                        {isBlocked ? 'Blocked' : (isActive ? "Active" : "Inactive")}
                                    </Text>
                                }
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>

            <div style={{marginTop: 20}}>
                <div style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    justifyContent: isMobile ? 'stretch' : 'flex-end'
                }}>
                    {isOwner && ['promoter', 'partner'].includes(role) && !isBlocked && (
                        <Button type="primary" block={isMobile} style={{width: isMobile ? '100%' : 'auto'}}
                                onClick={() => {
                                    setToggleActive(isActive);
                                    setIsModalVisible(true);
                                }}>
                            Update
                        </Button>)}
                    <Button type="default" block={isMobile} style={{width: isMobile ? '100%' : 'auto'}}
                            onClick={() => navigate(-1)}>
                        Back
                    </Button>
                </div>
            </div>

            <Modal title="Update Status" open={isModalVisible} onOk={handleUpdateLinkStatus}
                   onCancel={() => setIsModalVisible(false)} okText="Save"
            >
                <Switch checked={toggleActive} onChange={setToggleActive} checkedChildren="Active"
                        unCheckedChildren="Inactive"/>
            </Modal>
        </div>
    );
};

export default CampaignLinkDetailPage;
